import { useBlocker } from '@tanstack/react-router'
import { useRef } from 'react'

import { stringifySearchFromStrings } from './search'
import type { BeforeNavigateHandler } from './types'

/**
 * Public surface of the navigation shim: pages import their navigation
 * primitives from `@/lib/navigation/*` and nowhere else (apps/app/CLAUDE.md),
 * so these type names are part of that contract.
 *
 * @public
 */
export type { BeforeNavigateAction, BeforeNavigateHandler, BeforeNavigateVerdict } from './types'

/**
 * TanStack's blocker decides before the history entry is written, which is
 * the same moment Next's routeChangeStart fired. The handler sees the
 * destination as a URL string (pathname + search) like it did under Next.
 *
 * One asymmetry, deliberately accepted: `@tanstack/history` consults blockers
 * on PUSH and REPLACE only, so this does NOT fire on Back/Forward, where
 * Next's routeChangeStart did. The only consumer is GTMProvider's landing
 * guard, whose effect hard-navigates on arrival anyway — so a popstate into a
 * guarded route still ends up where it should, after a visible flash.
 *
 * The handler also learns whether the navigation pushes or replaces, so a
 * consumer that turns it into a full load can keep the same history shape.
 * The blocker only ever sees PUSH and REPLACE, so anything else maps to push.
 */
export function useBeforeNavigate(handler: BeforeNavigateHandler): void {
  const handlerRef = useRef(handler)
  handlerRef.current = handler
  useBlocker({
    shouldBlockFn: ({ next, action }) => {
      const url = `${next.pathname}${stringifySearchFromStrings((next.search ?? {}) as Record<string, unknown>)}`
      return handlerRef.current(url, action === 'REPLACE' ? 'replace' : 'push') === 'block'
    },
    enableBeforeUnload: false,
    withResolver: false,
  })
}
